<script setup lang="ts">
import "@maersk-global/mds-components-core-select";
import "@maersk-global/mds-components-core-option";
import { onMounted, computed, ref, watch } from "vue";
import { useImportLocationStore } from "@/store/import/import_location_store";
import type { CountryData, Terminal } from "@/interface/common/location_inteface";
import { getDataFromLS } from "@/utils/common";
import { useGlobalStore } from "@/store/global";

const locationStore = useImportLocationStore();
const globalStore = useGlobalStore();

// Data for regions, countries, and terminals
const regions = computed((): any => locationStore.filteredLocations);

const labels = {
  all: globalStore.sitecoreData.common.all,
  region: globalStore.sitecoreData.common.region,
  country: globalStore.sitecoreData.common.country,
  location: globalStore.sitecoreData.common.location,
  selectlocation: globalStore.sitecoreData.placeHolders.selectLocation
};

const allOption = labels.all;
const selectedRegion = ref<string | null>(allOption);
const selectedCountry = ref<string | null>(allOption);
const selectedTerminal = ref<string | null>(null);
const isManualChange = ref(false); // To track if the change is manual

onMounted(async () => {
  await locationStore.fetchImportLocations();
  const terminalSelected = getDataFromLS("saveContainerTerminal");
  const terminalFromUrl = new URLSearchParams(window.location.search).get("terminal") || "";
  if (terminalSelected) {
    selectedTerminal.value = terminalSelected.id;
    updateSelectionFromTerminal();
  } else if (terminalFromUrl !== "") {
    selectedTerminal.value = terminalFromUrl.toUpperCase();
    updateSelectionFromTerminal();
    updateTerminal();
  } else {
    selectedTerminal.value = locationStore.terminalSelected.id;
  }
});

// Function to update region and country based on selected terminal
const updateSelectionFromTerminal = (): void => {
  if (selectedTerminal.value) {
    for (const region of regions.value) {
      for (const country of region.countries) {
        const terminal = country.terminals.find((t) => t.id === selectedTerminal.value);
        if (terminal) {
          isManualChange.value = false; // Not a manual change
          selectedRegion.value = region.name;
          selectedCountry.value = country.name;
          return;
        }
      }
    }
  }
};

// Computed property to get available countries based on selected region
const availableCountries = computed((): CountryData[] => {
  if (selectedRegion.value === allOption) {
    return regions.value.flatMap((region): CountryData[] => region.countries);
  }
  const region = regions.value.find((r): boolean => r.name === selectedRegion.value);
  return region ? region.countries : [];
});

// Computed property to get available terminals based on selected country
const filteredTerminals = computed((): Terminal[] => {
  if (selectedRegion.value === allOption && selectedCountry.value !== allOption) {
    return regions.value.flatMap(
      (region): Terminal[] =>
        region.countries.find((country): boolean => country.name === selectedCountry.value)?.terminals || []
    );
  }
  if (selectedRegion.value === allOption && selectedCountry.value === allOption) {
    return regions.value.flatMap((region): Terminal[] =>
      region.countries.flatMap((country): Terminal[] => country.terminals)
    );
  }
  if (selectedRegion.value !== allOption && selectedCountry.value === allOption) {
    const region = regions.value.find((r): boolean => r.name === selectedRegion.value);
    return region ? region.countries.flatMap((country): Terminal[] => country.terminals) : [];
  }
  if (selectedRegion.value !== allOption && selectedCountry.value !== allOption) {
    const region = regions.value.find((r): boolean => r.name === selectedRegion.value);
    return region
      ? region.countries.find((country): boolean => country.name === selectedCountry.value)?.terminals || []
      : [];
  }
  return regions.value.flatMap((region): Terminal[] =>
    region.countries.flatMap((country): Terminal[] => country.terminals)
  );
});

// Function to handle terminal selection
const updateTerminal = (): void => {
  if (selectedTerminal.value === "") {
    locationStore.setTempLocation({} as Terminal);
  } else {
    const terminal = filteredTerminals.value.find((t) => t.id === selectedTerminal.value);
    if (terminal) {
      locationStore.setTempLocation(terminal);
    }
  }
};

// Watch terminal selection to update region and country
watch(selectedTerminal, (newTerminal) => {
  if (newTerminal) {
    updateSelectionFromTerminal();
  }
});

// Handle manual changes in region and country
const handleManualChange = (): void => {
  isManualChange.value = true;
};

// Watch for manual changes in selected region
watch(selectedRegion, () => {
  if (isManualChange.value) {
    selectedCountry.value = allOption; // Reset country
    selectedTerminal.value = null; // Reset terminal
    locationStore.setTempLocation("");
  }
});

// Watch for manual changes in selected country
watch(selectedCountry, () => {
  if (isManualChange.value) {
    selectedTerminal.value = null; // Reset terminal
    locationStore.setTempLocation("");
  }
});
</script>

<template>
  <div class="country-grid-listing">
    <!-- Region Selection -->
    <div class="grid-item">
      <mc-select v-model="selectedRegion" :label="labels.region" @optionselected="handleManualChange">
        <mc-option :value="allOption">{{ labels.all }}</mc-option>
        <mc-option v-for="region in regions" :key="region.name" :value="region.name">{{ region.name }}</mc-option>
      </mc-select>
    </div>

    <!-- Country Selection -->
    <div class="grid-item">
      <mc-select v-model="selectedCountry" :label="labels.country" @optionselected="handleManualChange">
        <mc-option :value="allOption">{{ labels.all }}</mc-option>
        <mc-option v-for="country in availableCountries" :key="country.name" :value="country.name">
          {{ country.name }}
        </mc-option>
      </mc-select>
    </div>

    <!-- Terminal Selection -->
    <div class="grid-item">
      <mc-select
        v-model="selectedTerminal"
        :label="labels.location"
        required
        :placeholder="labels.selectlocation"
        @optionselected="updateTerminal"
      >
        <mc-option v-for="terminal in filteredTerminals" :key="terminal.id" :value="terminal.id">
          {{ terminal.name }}
        </mc-option>
      </mc-select>
    </div>
  </div>
</template>
